import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
    EXPORT_FORMATS,
    FREQUENCY_KEYS,
    FrequencyKey,
    INTERVAL_HOURS,
    ScheduleFormState,
    TIMEZONES,
    formatCadenceLong,
    toVariables,
    zoneName,
} from './scheduleFormat.ts';
import Alert from '../../../core/components/Alert.tsx';
import Checkbox from '../../../core/components/Checkbox.tsx';
import DateField from '../../../core/components/Fields/DateField.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import TextField from '../../../core/components/Fields/TextField.tsx';
import TimeField from '../../../core/components/Fields/TimeField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import SegmentedButton from '../../../core/components/SegmentedButton.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { RecipientsField } from '../RecipientsField.tsx';

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, index) => String(index + 1));

// the default end date when switching to "On date": a month from today
const inAMonth = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

interface CheckboxRowProps {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    label: string;
    hint?: string;
    isDisabled?: boolean;
}

const CheckboxRow = ({ checked, onCheckedChange, label, hint, isDisabled = false }: CheckboxRowProps) => (
    <label className={classnames('flex items-start gap-2', isDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
        <Checkbox
            className="mt-0.5 flex-none"
            checked={checked}
            disabled={isDisabled}
            onChange={(event) => onCheckedChange(event.target.checked)}
        />
        <span className="flex min-w-0 flex-col gap-0.5">
            <span className={classnames('text-sm', isDisabled ? 'text-content-weak' : 'text-content-strong')}>
                {label}
            </span>
            {hint && <span className="text-xs text-content-medium">{hint}</span>}
        </span>
    </label>
);

interface ScheduleFormProps {
    form: ScheduleFormState;
    onChange: (patch: Partial<ScheduleFormState>) => void;
    isNameGenerating?: boolean;
    onPromptBlur?: () => void;
    mailbox?: { address: string | null; connected: boolean } | null;
}

const ScheduleForm = ({
    form,
    onChange,
    isNameGenerating = false,
    onPromptBlur,
    mailbox = null,
}: ScheduleFormProps) => {
    const { t } = useTranslation('chat');

    const weekdayOptions = (t('schedule.weekdays', { returnObjects: true }) as string[]).map((label, value) => ({
        value: String(value),
        label,
    }));
    const dayOptions = DAYS_OF_MONTH.map((day) => ({ value: day, label: day }));
    const intervalOptions = INTERVAL_HOURS.map((hours) => ({
        value: String(hours),
        label: t('automations.dialog.hours', { count: hours }),
    }));
    const timezoneOptions = (TIMEZONES.includes(form.timezone) ? TIMEZONES : [...TIMEZONES, form.timezone]).map(
        (zone) => ({ value: zone, label: zone })
    );

    const nextRun = `${formatCadenceLong(t, toVariables(form))} · ${t('automations.detail.timezoneLabel', {
        zone: zoneName(form.timezone),
    })}`;

    return (
        <div className="flex flex-col gap-5">
            <FormField label={t('automations.dialog.name')} required>
                <StringField
                    value={form.name}
                    disabled={isNameGenerating}
                    placeholder={
                        isNameGenerating
                            ? t('automations.dialog.nameGenerating')
                            : t('automations.dialog.namePlaceholder')
                    }
                    onValueChange={(name) => onChange({ name })}
                />
            </FormField>

            <FormField label={t('automations.dialog.prompt')} required>
                <TextField
                    rows={3}
                    value={form.prompt}
                    onValueChange={(prompt) => onChange({ prompt })}
                    onBlur={onPromptBlur}
                />
            </FormField>

            <FormField label={t('automations.dialog.frequency')}>
                <SegmentedButton
                    variant="joined"
                    value={form.frequency}
                    onValueChange={(frequency) => onChange({ frequency: frequency as FrequencyKey })}
                >
                    {FREQUENCY_KEYS.map((key) => (
                        <SegmentedButton.Item key={key} value={key}>
                            {t(`automations.dialog.frequencies.${key}`)}
                        </SegmentedButton.Item>
                    ))}
                </SegmentedButton>
            </FormField>

            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap gap-3">
                    {form.frequency === 'weekly' && (
                        <FormField label={t('automations.dialog.weekday')} className="w-40">
                            <SelectField
                                required
                                options={weekdayOptions}
                                value={String(form.weekday)}
                                onValueChange={(value) => onChange({ weekday: Number(value) })}
                            />
                        </FormField>
                    )}
                    {form.frequency === 'monthly' && (
                        <FormField label={t('automations.dialog.onDay')} className="w-24">
                            <SelectField
                                required
                                options={dayOptions}
                                value={String(form.dayOfMonth)}
                                onValueChange={(value) => onChange({ dayOfMonth: Number(value) })}
                            />
                        </FormField>
                    )}
                    {form.frequency === 'custom' ? (
                        <FormField label={t('automations.dialog.every')} className="w-32">
                            <SelectField
                                required
                                options={intervalOptions}
                                value={String(form.intervalHours)}
                                onValueChange={(value) => onChange({ intervalHours: Number(value) })}
                            />
                        </FormField>
                    ) : (
                        <FormField label={t('automations.dialog.at')} className="w-28">
                            <TimeField value={form.timeOfDay} onValueChange={(timeOfDay) => onChange({ timeOfDay })} />
                        </FormField>
                    )}
                </div>
                {form.frequency === 'monthly' && (
                    <CheckboxRow
                        checked={form.useLastDayIfMissing}
                        onCheckedChange={(useLastDayIfMissing) => onChange({ useLastDayIfMissing })}
                        label={t('automations.dialog.lastDay')}
                    />
                )}
            </div>

            <div className="flex flex-col gap-3">
                <FormField label={t('automations.dialog.ends')}>
                    <SegmentedButton
                        variant="joined"
                        value={form.endsMode}
                        onValueChange={(value) => {
                            const endsMode = value === 'date' ? 'date' : 'never';
                            onChange(
                                endsMode === 'date' ? { endsMode, endsOn: form.endsOn ?? inAMonth() } : { endsMode }
                            );
                        }}
                    >
                        <SegmentedButton.Item value="never">{t('automations.dialog.endsNever')}</SegmentedButton.Item>
                        <SegmentedButton.Item value="date">{t('automations.dialog.endsOn')}</SegmentedButton.Item>
                    </SegmentedButton>
                </FormField>
                {form.endsMode === 'date' && (
                    <FormField
                        className="w-40"
                        error={form.endsOn ? undefined : { message: t('automations.dialog.endDateRequired') }}
                    >
                        <DateField
                            aria-label={t('automations.dialog.endsOn')}
                            value={form.endsOn ?? undefined}
                            fromDate={new Date()}
                            onValueChange={(endsOn) => onChange({ endsOn: endsOn || null })}
                        />
                    </FormField>
                )}
            </div>

            <FormField label={t('automations.dialog.timezone')} className="w-64 max-w-full">
                <SelectField
                    required
                    options={timezoneOptions}
                    value={form.timezone}
                    onValueChange={(timezone) => onChange({ timezone })}
                />
            </FormField>

            <FormField label={t('automations.dialog.shareWith')} helpText={t('automations.dialog.shareWithHelp')}>
                <RecipientsField value={form.recipients} onValueChange={(recipients) => onChange({ recipients })} />
            </FormField>

            <div className="flex flex-col gap-3">
                <CheckboxRow
                    checked={form.emailMe}
                    onCheckedChange={(emailMe) => onChange({ emailMe })}
                    label={t('automations.dialog.emailMe')}
                    hint={
                        mailbox
                            ? t(
                                  mailbox.connected
                                      ? 'automations.dialog.emailMeHelp'
                                      : 'automations.dialog.emailMeHelpOnBehalf',
                                  { mailbox: mailbox.address }
                              )
                            : undefined
                    }
                    isDisabled={!mailbox}
                />
                {form.emailMe && mailbox && (
                    <div className="pl-7">
                        <FormField label={t('automations.dialog.attachAs')}>
                            <SegmentedButton
                                variant="joined"
                                value={form.exportFormat}
                                onValueChange={(exportFormat) => onChange({ exportFormat })}
                            >
                                {EXPORT_FORMATS.map((format) => (
                                    <SegmentedButton.Item key={format} value={format}>
                                        {t(`automations.formats.${format}`)}
                                    </SegmentedButton.Item>
                                ))}
                            </SegmentedButton>
                        </FormField>
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-3">
                <span className="text-xs text-content-medium">{t('automations.dialog.notifications')}</span>
                <CheckboxRow
                    checked={form.notifyOnRun}
                    onCheckedChange={(notifyOnRun) => onChange({ notifyOnRun })}
                    label={t('automations.dialog.notifyLabel')}
                    hint={t('automations.dialog.notifyHelp')}
                />
            </div>

            <Alert severity="info">
                <div className="flex flex-col gap-0.5">
                    <span className="font-medium">{t('automations.dialog.nextRun', { when: nextRun })}</span>
                    <span>{t('automations.dialog.unattended')}</span>
                </div>
            </Alert>
            {/* last, right above the buttons: it changes what saving does (the button then reads "… & test") */}
            <CheckboxRow
                checked={form.testRun}
                onCheckedChange={(testRun) => onChange({ testRun })}
                label={t('automations.dialog.testRun')}
                hint={t('automations.dialog.testRunHint')}
            />
        </div>
    );
};

export default ScheduleForm;
